import { SalesActionsType } from 'api-hooks/general/model';
import { ProductSalesModel } from 'api-hooks/product/sales';
import { ActivitySaleModel } from 'api-hooks/sale/activity';
import { SaleModel } from 'api-hooks/sale/common';
import { CruiseSaleModel } from 'api-hooks/sale/cruise';
import { HotelSaleModel } from 'api-hooks/sale/hotel';
import { TicketSaleModel } from 'api-hooks/sale/ticket-sale';
import { TravelDocumentSalesModel } from 'api-hooks/sale/travel-document';
import { TourSalesModel } from 'api-hooks/tour/tour-sale';
import { AuthorizationRules, NavigationRoutes } from 'common/constants';
import { SaleStatusEnum } from 'common/constants/enum';
import { FormAutoSaver } from 'common/helpers/form-auto-saver';
import { PopoverMenuType } from 'components/popover';
import { useAuthorization } from 'hooks/use-authorization';
import useNavigation from 'hooks/use-navigation';
import useTranslation from 'next-translate/useTranslation';
import React from 'react';

export default function useSalesToOrder(
  type: SalesActionsType,
  data: SaleModel,
) {
  const { navigate } = useNavigation();
  const { t } = useTranslation();
  const { can } = useAuthorization();

  const status = data.status;
  const isCompleted = data.isCompleted;
  const isAllowed = React.useMemo(() => {
    switch (status) {
      case SaleStatusEnum.Active:
      case SaleStatusEnum.Completed:
      case SaleStatusEnum.OpenTicket:
      case SaleStatusEnum.Rescheduled:
        return !isCompleted;
      default:
        return false;
    }
  }, [isCompleted, status]);

  const permission = React.useMemo(() => {
    switch (type) {
      case 'flight-sales':
        return can(AuthorizationRules.FlightPurchasesCreate);
      case 'hotel-sales':
        return can(AuthorizationRules.HotelPurchasesCreate);
      case 'cruise-sales':
        return can(AuthorizationRules.CruisePurchasesCreate);
      case 'document-sales':
        return can(AuthorizationRules.DocumentPurchasesCreate);
      case 'activity-sales':
        return can(AuthorizationRules.ActivityPurchasesCreate);
      //no order
      case 'product-sales':
      case 'tour-sales':
        return false;
    }
  }, [can, type]);

  const createOrderRoute = React.useMemo(() => {
    switch (type) {
      case 'flight-sales':
        return NavigationRoutes.FlightPurchasesCreate;
      case 'hotel-sales':
        return NavigationRoutes.HotelPurchasesCreate;
      case 'cruise-sales':
        return NavigationRoutes.CruisePurchasesCreate;
      case 'document-sales':
        return NavigationRoutes.DocumentPurchasesCreate;
      case 'activity-sales':
        return NavigationRoutes.ActivityPurchasesCreate;
      case 'product-sales':
        return NavigationRoutes.ProductPurchasesCreate;
      case 'tour-sales':
        return NavigationRoutes.TourSalesCreate;
    }
  }, [type]);

  const orderPermission = isAllowed && permission;

  const onCreateOrder = React.useCallback(() => {
    FormAutoSaver.remove(createOrderRoute);
    navigate(createOrderRoute, {
      cruiseSale: data as CruiseSaleModel,
      documentSale: data as TravelDocumentSalesModel,
      flightSale: data as TicketSaleModel,
      hotelSale: data as HotelSaleModel,
      activitySale: data as ActivitySaleModel,
      productSale: data as ProductSalesModel,
      tourSale: data as TourSalesModel,
    });
  }, [createOrderRoute, data, navigate]);

  const createOrderAction: PopoverMenuType = {
    label: t('common:create_extra', { extra: t('common:order') }),
    onClick: onCreateOrder,
    isAllowed: orderPermission,
    isError: false,
    key: `create-order-${data.id}`,
  };

  return {
    createOrderAction,
    onCreateOrder,
    orderPermission,
    createOrderRoute,
  };
}
